// Relative, with an explicit extension, rather than the `@/` alias: this
// module is imported by `docReaderProfiles.test.js` under plain
// `node --test`, which does not resolve Vite aliases. Same reason
// `utils/hasRole.js` imports `./setUtils.js` that way.
import { hasRole } from '../../utils/hasRole.js'
import {
    AUDIENCE_ROLES,
    resolveDocAudience,
    SITE_ADMIN_ROLE
} from './docAccessRules.js'

/**
 * Browser-facing wrapper around `docAccessRules.js`.
 *
 * IMPORTANT: this is a nav-parity boundary, not a confidentiality one.
 * Every MDX page and every search index shard still ships to every
 * authenticated browser as a lazy chunk via `import.meta.glob` — that is
 * how the docs viewer and the search index loader both work. A determined
 * reader can fetch a chunk this map would refuse to render just by asking
 * for its URL. `canViewAudience`/`canViewDoc` govern which sidebar entries
 * appear, whether `DocsMdxView` serves a page on direct navigation, and
 * which search shards get loaded — not what the browser can be made to
 * download.
 *
 * Do not put anything in `frontend/docs/**` that depends on this map for
 * secrecy. `/docs/*` is additionally route-gated in `App.jsx`, and any
 * real data referenced from a doc is gated on the backend.
 */

/** Can this user see content gated to `audience`? */
export function canViewAudience(audience, user) {
    // Never admit on an unresolved session.
    if (!user) return false

    // Site admins short-circuit every audience — they answer support
    // questions about workspaces they hold no grant for. `hasRole` reads
    // `user.roles` unguarded, so check it has entries before calling.
    if (user.roles?.length && hasRole(user, [SITE_ADMIN_ROLE])) return true

    if (!(audience in AUDIENCE_ROLES)) return false

    const roles = AUDIENCE_ROLES[audience]
    // `member`: any authenticated docs reader.
    if (roles === null) return true

    if (!user.roles?.length) return false
    return hasRole(user, roles)
}

/** Can this user see the doc at `path` (a `/docs/...` path, no `.mdx`)? */
export function canViewDoc(path, user) {
    return canViewAudience(resolveDocAudience(path), user)
}
